import { Search } from "lucide-react";
import { CrmListChevron } from "@/components/crm/CrmListChevron";
import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { createPortal } from "react-dom";
import {
  ISO_COUNTRIES,
  filterIsoCountries,
  formatIsoCountryLabel,
  getIsoCountryByCode,
  type IsoCountry,
} from "@/lib/address/isoCountries";

type AddressCountryPickerProps = {
  value: string;
  onChange: (countryCode: string) => void;
  disabled?: boolean;
  ariaLabel?: string;
};

type MenuPosition = { top: number; left: number; width: number };

const MENU_WIDTH_PX = 320;
const MENU_GAP_PX = 4;

function computeMenuPosition(trigger: HTMLElement): MenuPosition {
  const rect = trigger.getBoundingClientRect();
  const width = Math.min(MENU_WIDTH_PX, window.innerWidth - 16);
  let left = rect.left;
  if (left + width > window.innerWidth - 8) {
    left = Math.max(8, window.innerWidth - width - 8);
  }
  return { top: rect.bottom + MENU_GAP_PX, left, width };
}

export function AddressCountryPicker({
  value,
  onChange,
  disabled,
  ariaLabel = "Country",
}: AddressCountryPickerProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);

  const selected = getIsoCountryByCode(value) || ISO_COUNTRIES.find((c) => c.code === "us") || ISO_COUNTRIES[0];
  const filtered = useMemo(() => filterIsoCountries(query), [query]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setHighlight(0);
    const t = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) {
      setMenuPosition(null);
      return;
    }
    const update = () => {
      if (!triggerRef.current) return;
      setMenuPosition(computeMenuPosition(triggerRef.current));
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open]);

  useEffect(() => {
    if (highlight >= filtered.length) setHighlight(filtered.length ? filtered.length - 1 : 0);
  }, [filtered.length, highlight]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (document.getElementById(listId)?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, listId]);

  const pick = (country: IsoCountry) => {
    onChange(country.code);
    setOpen(false);
  };

  const menu =
    open && menuPosition
      ? createPortal(
          <div
            id={listId}
            className="fixed z-[200] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg dark:border-white/15 dark:bg-zinc-900"
            style={{ top: menuPosition.top, left: menuPosition.left, width: menuPosition.width }}
            role="presentation"
          >
            <div className="flex items-center gap-2 border-b border-slate-200 px-2 py-2 dark:border-white/10">
              <Search className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setHighlight(0);
                }}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e.key === "Escape") {
                    e.preventDefault();
                    setOpen(false);
                    return;
                  }
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setHighlight((i) => (filtered.length ? (i + 1) % filtered.length : 0));
                    return;
                  }
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setHighlight((i) => (filtered.length ? (i - 1 + filtered.length) % filtered.length : 0));
                    return;
                  }
                  if (e.key === "Enter") {
                    e.preventDefault();
                    const row = filtered[highlight];
                    if (row) pick(row);
                  }
                }}
                placeholder="Search country"
                className="min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400 dark:text-white"
                aria-label="Search countries"
              />
            </div>
            <ul role="listbox" aria-label="Countries" className="max-h-56 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <li className="px-3 py-2 text-sm text-slate-500">No matches</li>
              ) : (
                filtered.map((country, index) => (
                  <li key={country.code} role="option" aria-selected={country.code === value}>
                    <button
                      type="button"
                      className={
                        index === highlight
                          ? "flex w-full items-center gap-2 bg-indigo-50 px-3 py-2 text-left text-sm text-slate-900 dark:bg-indigo-950/50 dark:text-white"
                          : "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-white/5"
                      }
                      onMouseEnter={() => setHighlight(index)}
                      onClick={() => pick(country)}
                    >
                      <span aria-hidden>{country.flag}</span>
                      <span className="min-w-0 flex-1 truncate">{formatIsoCountryLabel(country)}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className="relative w-full">
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? listId : undefined}
        onClick={() => !disabled && setOpen((v) => !v)}
        className="inline-flex w-full items-center justify-between gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm text-slate-800 hover:bg-gray-50 disabled:opacity-50 dark:border-white/20 dark:bg-black/20 dark:text-slate-100 dark:hover:bg-white/5"
      >
        <span className="flex min-w-0 items-center gap-2 truncate">
          <span aria-hidden>{selected.flag}</span>
          <span className="truncate">{formatIsoCountryLabel(selected)}</span>
        </span>
        <CrmListChevron />
      </button>
      {menu}
    </div>
  );
}

export { formatIsoCountryLabel };
